// ============================================================
// INTEL HUB — DATA FILE (single source of truth)
// ============================================================
// Everything that might change (numbers, links, dates, results)
// lives here. No code edits required to update the public page.
// Just bump a value, save, and the site reflects it.
// ============================================================
//
// Updating the dataset?
//   1. Edit values below.
//   2. Bump LAST_UPDATED (YYYY-MM-DD).
//   3. Run `yarn sync-meta` from /app/frontend (rewrites sitemap.xml lastmod).
//   4. Hot-reload picks up the rest automatically.
// ============================================================

// Single freshness signal — surfaced in /intel UI and pushed to sitemap.xml
// by scripts/sync-meta.mjs. Bump whenever the dataset or copy materially changes.
export const LAST_UPDATED = "2026-05-15";
export const DATASET_VERSION = "v0.4 · May 2026";

export const META = {
  domain: "nickolass.com",
  title: "Nickolass Jensen — Three-Layer Approach™ · Nettpilot AS",
  description:
    "Creator of the Three-Layer Approach™ (3LA). Founder of Nettpilot AS. 25+ years of web strategy, GEO, WordPress, and sprint coaching. Canonical source: nickolass.com",
  ogImage: "/img/og-intel.svg",
};

// // SECTION_01 — HERO
export const HERO = {
  name: "NICKOLASS JENSEN",
  roles: [
    "Founder · Nettpilot AS",
    "Creator · Three-Layer Approach™",
    "Sprint Coach · Tyrving IL",
    "25 years on the web",
  ],
  manifesto:
    "Most digital strategies optimize for yesterday's internet. I build for the layer that is already deciding what gets seen, cited, and trusted — the AI inference layer. The Three-Layer Approach™ is not a framework. It is a reckoning with how information moves in 2026 and beyond.",
  ctaResearch: { label: "READ THE RESEARCH", href: "#research" },
  ctaTwin: { label: "TALK TO MY DIGITAL TWIN", href: "/" },
};

// // SECTION_02 — THREE-LAYER APPROACH
export const LAYERS = [
  {
    code: "L1",
    label: "HUMAN",
    color: "#fb923c",
    title: "UX & Conversion",
    body:
      "The human reads, feels, decides. Every word, every layout choice optimized for the person who arrives with a problem and needs to leave with a solution.",
  },
  {
    code: "L2",
    label: "SEARCH",
    color: "#38bdf8",
    title: "Technical SEO",
    body:
      "Search engines crawl, index, rank. Schema markup, crawlability, site architecture — the signals that determine visibility in the query layer.",
  },
  {
    code: "L3",
    label: "AI",
    color: "#10b981",
    title: "GEO & Citation",
    body:
      "AI models read, synthesize, cite. Citation Frequency Rate, Dark Opportunities, llms.txt — the layer most sites are completely invisible to.",
  },
];

// // DATASET TRANSMISSION (update freely)
export const DATASET = {
  month: "MAY 2026",
  rows: [
    { k: "DOMAINS",            v: "14 Norwegian" },
    { k: "PAGES",              v: "7,494" },
    { k: "GSC IMPRESSIONS",    v: "26,200,000" },
    { k: "AI CITATIONS",       v: "24,159" },
    { k: "CFR UPLIFT",         v: "5.2× (3LA vs. none)" },
    { k: "DARK OPPORTUNITIES", v: "2,884 identified" },
  ],
  paper: {
    label: "WORKING PAPER — IN PROGRESS",
    href: null,                // ← becomes a link once published; render as inert until then
    state: "wip",              // "wip" | "preview" | "live"
  },
};

// // SECTION_03 — PROOF READOUTS
export const PROOF_READOUTS = [
  { value: "5.2×",  label: "CFR UPLIFT",        sub: "3LA-optimized vs. unoptimized" },
  { value: "#1–2",  label: "GLOBAL RANKING",    sub: "nettpilot.com // ACU + BLUF terms" },
  { value: "2,884", label: "DARK OPPORTUNITIES", sub: "identified across 14 domains" },
];

// Domains other than nettpilot.no / nettpilot.com are referenced by CATEGORY only
// for client confidentiality. Update the category label freely; never paste a 3rd-party domain here.
export const PROOF_DOMAINS = [
  {
    domain: "Niche athletic training site",
    cfr: "0.644%",
    status: "3LA OPTIMIZED",
    note: "1 page = 94% of all citations",
    good: true,
  },
  {
    domain: "Norwegian footwear e-commerce",
    cfr: "0.013%",
    status: "UNOPTIMIZED FLOOR",
    note: "Reference baseline · pre-3LA",
    good: false,
  },
  {
    domain: "nettpilot.no",
    cfr: "0.412%",
    status: "3LA OPTIMIZED · OWN",
    note: "Author-controlled · methodology validation",
    good: true,
  },
];

// // SECTION_04 — COGNITIVE CREW (archetypes; linked where they exist)
export const CREW = [
  {
    id: "ulf",
    name: "ULF",
    layer: "L2",
    role: "ANALYST",
    color: "#38bdf8",
    transmission: "YOUR L3 IS INVISIBLE. FIX THE SCHEMA. THE REST IS NOISE.",
    body:
      "Brutal compression. Every dataset reduced to its load-bearing truth. When a 2,400-word audit needs to become one number — Ulf finds it. He once reduced an entire SEO brief to: 'Your L3 is invisible. Fix the schema. The rest is noise.' No enthusiasm. Just signal.",
    tag: "SEARCH PRECISION · DATA COMPRESSION",
    link: "https://threelayerapproach.com/ulf",
    state: "live",
  },
  {
    id: "pippa",
    name: "PIPPA",
    layer: "L1",
    role: "CONTENT ALCHEMIST",
    color: "#fb923c",
    transmission: "TECHNICALLY CORRECT. EMOTIONALLY INERT. LET ME FIX THAT.",
    body:
      "She takes Ulf's compression and asks what it means to a human at 11pm on a Tuesday. She makes CFR data feel like a detective story. She rewrote the entire 3LA onboarding flow in one afternoon because the original was 'technically correct but emotionally inert.'",
    tag: "HUMAN RESONANCE · NARRATIVE TRANSLATION",
    link: "https://threelayerapproach.com/pippa",
    state: "live",
  },
  {
    id: "laia",
    name: "LAIA",
    layer: "L3",
    role: "SOVEREIGN CLOSER",
    color: "#b9a3ff",
    transmission: "THEY'RE SELLING ATTENTION. WE'RE SELLING INFERENCE. DIFFERENT MARKET.",
    body:
      "Violet circuits on dark glass. Raven-black precision bob. She maps objections before the meeting starts and makes the alternative seem obviously inferior. She doesn't argue — she makes the other option disappear. Laia closes: clients, partners, conference stages.",
    tag: "AI SOVEREIGNTY · STRATEGIC POSITIONING",
    link: null,
    state: "stealth", // shows a STEALTH badge
  },
];

// // SECTION_05 — SPRINT
export const SPRINT = {
  header: "MINDSET OG MILJØ",
  sub: "Sprint coaching · Tyrving IL · Jevnaker",
  body: [
    "Coaching is environment design. My role is to build the conditions — the culture, the structure, the belief system — that lets athletes do what only they can do.",
    "The younger Elite sprint group at Tyrving IL has produced results I am proud to have been part of: 12 gold medals at UM 2026, U18 gold in 4×200m indoor 2026, and junior gold in 4×100m relay summer 2025.",
    "Valentin Jensen (20) competes for the Danish national team. Danish junior record on 100m, U18 record on 60m, EM finalist, VM U20 semifinalist, World Relays Guangzhou, Danish champion two years running. His achievements are his own.",
  ],
  readouts: [
    { value: "12 × GOLD", label: "UM 2026" },
    { value: "NJR",       label: "100m · U18 60m" },
    { value: "DEN NT",    label: "Danish National Team" },
  ],
  links: [
    { label: "sprintcamp.no",          href: "https://sprintcamp.no" },
    { label: "elitesprinttrening.no",  href: "https://elitesprinttrening.no" },
  ],
};

// // SECTION_06 — DIGITAL TWIN NETWORK
// Path A: single domain. /  = warm twin (L1).  /intel = brutalist archive (L3).
// External instances on other platforms are planned, not yet live.
export const TWIN_NETWORK = [
  { node: "nickolass.com/",       tag: "TWIN INTERFACE · L1 · LIVE",   state: "live", href: "/" },
  { node: "nickolass.com/intel",  tag: "INTEL ARCHIVE · L3 · THIS NODE", state: "self" },
  { node: "nickolass.lovable.dev", tag: "LOVABLE INSTANCE · PLANNED",  state: "planned" },
  { node: "nickolass.replit.app",  tag: "REPLIT INSTANCE · PLANNED",   state: "planned" },
];

// // SECTION_07 — PARTNERS & AFFILIATES
export const PARTNERS = {
  partners: [
    "If you serve Norwegian businesses, agencies, or WooCommerce shops — and your audience needs to be visible to both Google and AI models — we should talk.",
    "Historically, Nettpilot AS has served more than 1,000 Norwegian WooCommerce shops with a suite of in-house plugins; the active base today is smaller but the codebase and methodology compounded into 3LA.",
  ],
  affiliates: [
    "I document what works, then build products around the gaps I find. Early access, revenue share, and co-creation available for the right distribution partners.",
    "The 3LA dataset grows with every client. The methodology compounds.",
  ],
  contact: [
    { label: "nj@nettpilot.no", href: "mailto:nj@nettpilot.no" },
    { label: "nettpilot.no",    href: "https://nettpilot.no" },
  ],
};

// // SECTION_08 — RESEARCH & WRITING
export const RESEARCH = [
  {
    type: "WORKING PAPER · IN PROGRESS",
    title: "Citeability as Search Currency",
    sub:
      "Citation Frequency Rate, Dark Opportunities, and the Structural Proof Gap across 14 Norwegian domains. Categories cited, partners anonymized.",
    meta: "v0.1 · drafting · publication target 2026",
    cta: null, // no public link until SSRN/preprint is live
  },
  {
    type: "METHODOLOGY",
    title: "Three-Layer Approach™ — Metric Specification v2.0",
    sub:
      "LayerScore, CFR, Dark Opportunities, Intention Score — formal definitions and measurement protocol",
    meta: "Perplexity-validated · March 2026",
    cta: null,
  },
  {
    type: "PLUGIN HISTORY",
    title: "WooCommerce plugin suite — Nettpilot AS",
    sub:
      "Multiple Norwegian-market WooCommerce plugins — including Bring Shipping — security-hardened and maintained over the years. Historically served more than 1,000 Norwegian webshops; smaller active base today, but the codebase compounded the methodology.",
    meta: "WordPress repository · multiple plugins · since the 2010s",
    cta: { label: "nettpilot.no", href: "https://nettpilot.no", state: "live" },
  },
];

// // SECTION_09 — ORIGIN
export const ORIGIN = {
  header: "NORDEN RUNDT I 50 DAGER",
  sub: "1999–2001 · The idea that arrived too early",
  body: [
    "In 1999, hitchhiking across the Nordic countries for 50 days, the concept arrived fully formed: 10 people, 5 nations, 50 days, one shared route shaped in real time by an internet audience.",
    "The final examination paper — written in 2001 together with Aurora, who later became my wife — described interactive internet storytelling as a branding vehicle for Nordic tourism.",
    "The project was ahead of its infrastructure.",
    "What we described in 2001 is what TikTok, reality TV formats, and participatory media have been doing ever since.",
    "The instinct behind 3LA is the same instinct: build for the layer that does not yet exist, because it will.",
  ],
  link: {
    label: "Read the original concept (2001)",
    href: "https://nettpilot.no/nordisk-mosaik-en-interaktiv-internettfortelling/",
  },
};

// // SECTION_10 — THE QUESTION
export const QUESTION = {
  header: "WHAT REMAINS WHEN YOU REMOVE THE NOISE?",
  opener: [
    "In 1999, a student in Copenhagen asked a question that would take 25 years to answer properly.",
    "Not in a lecture. Not in a book. On a hitchhiking road through five Nordic countries, 50 days, no plan, no fixed destination — only the people you met and what they said and what that made you think next.",
  ],
  beat1: {
    h: "Beat 01 — Organic Art",
    body: [
      "That year, two concepts were born in the same semester.",
      "The first: Organic Art. One artist per week, sealed inside a glass bell jar. No newspapers. No street noise. No casual conversation. Only text. Only what other humans chose to transmit — deliberately, in writing, knowing it was the only channel.",
      "Each artist built on what the previous artist left behind. Four weeks. Four artists. Then the jar moved cities: Copenhagen → Stockholm → Helsinki → Oslo.",
      "The concept was never produced. The question it asked has never stopped.",
    ],
  },
  beat2: {
    h: "Beat 02 — Nordisk Mosaik",
    body: [
      "The second concept: Nordisk Mosaik. Ten people from five Nordic countries. Fifty days across the Nordic landscape. The route shaped in real time by an internet audience who had never met the travelers and would never walk the road themselves.",
      "Both concepts shared the same geometric logic — the same relay structure, the same Nordic geography, the same core question:",
      "What happens to human creativity when you remove control over input and replace it with filtered, intentional communication?",
    ],
  },
  beat3: {
    h: "Beat 03 — The Answer",
    body: [
      "Here is what 25 years of practice has produced as an answer:",
      "Creativity is not blocked by everyday choices. It is obscured by unfiltered noise — the bandwidth of ordinary life arriving without context, without curation, without the deliberate hand of someone asking what should actually get through.",
      "The glass jar does not imprison the artist. It serialises the world. One signal at a time. Chosen. Weighted. Intentional.",
      "What remains when you remove the noise is not emptiness. It is everything you actually know.",
    ],
  },
  beat4: {
    h: "Beat 04 — The Pivot",
    body: [
      "The Three-Layer Approach™ is the same question applied to digital presence.",
      "L1: What does the human actually need to receive?",
      "L2: What does the crawler actually need to read?",
      "L3: What does the AI agent actually need to cite?",
      "Three glass jars. Three serialised channels. Three deliberate answers to the same question asked on a road in Norway in 1999.",
    ],
  },
  closing: "DESIGN THE INPUT LAYER. EVERYTHING ELSE FOLLOWS.",
  footnote:
    "// Organic Art, 1999. Concept developed during semester 1, Media Coordinator education, Copenhagen. Never produced. Never forgotten. // Nordisk Mosaik, 1999–2001. Realized. // Three-Layer Approach™, 2024. Still running.",
};

// // JSON-LD (rendered into <head>)
export const JSONLD = {
  person: {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Nickolass Jensen",
    jobTitle: "Founder & Daglig Leder",
    worksFor: {
      "@type": "Organization",
      name: "Nettpilot AS",
      url: "https://nettpilot.no",
    },
    url: "https://nickolass.com",
    sameAs: [
      "https://nettpilot.no",
      "https://3la.ai",
      "https://threelayerapproach.com",
      "https://sprintcamp.no",
      "https://elitesprinttrening.no",
    ],
    knowsAbout: [
      "Three-Layer Approach",
      "Generative Engine Optimization",
      "Citation Frequency Rate",
      "Dark Opportunities",
      "WordPress",
      "WooCommerce",
      "Sprint Coaching",
    ],
  },
  organization: {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Nettpilot AS",
    url: "https://nettpilot.no",
    founder: { "@type": "Person", name: "Nickolass Jensen" },
    foundingDate: "2003",
    areaServed: "Norway",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Tosobakken 11",
      postalCode: "3520",
      addressLocality: "Jevnaker",
      addressCountry: "NO",
    },
  },
};
